// Public, unauthenticated demonstration of the SMS opt-in flow.
//
// Exists for carrier / A2P 10DLC review: /register is behind auth, so a
// reviewer following a link there is redirected to the landing page and cannot
// verify how consent is collected (Twilio error 30909 / 30921). This page shows
// the exact consent language and control used in registration, with the
// required disclosures, and needs no account to view.
//
// Keep the consent copy here identical to src/pages/Register.js — if that
// wording changes, change it here too, or the demonstration stops matching
// what members actually see.
import React from 'react';
import { Link } from 'react-router-dom';
import { useBranding } from '../contexts/BrandingContext';

const GOLD = '#C9A84C';

const Section = ({ title, children }) => (
  <section style={{ marginBottom: 28 }}>
    <h2 style={{ color: GOLD, fontSize: '1.05rem', margin: '0 0 10px' }}>{title}</h2>
    {children}
  </section>
);

function SmsOptIn() {
  const branding = useBranding();
  return (
    <div style={{ minHeight: '100vh', background: '#0D1E35', color: 'rgba(255,255,255,0.85)', padding: '40px 20px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', lineHeight: 1.65 }}>
        <h1 style={{ color: 'white', fontSize: '1.6rem', marginBottom: 4 }}>
          SMS Opt-In — Torch Beach Volleyball LLC
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.55)', marginTop: 0 }}>
          How members consent to receive text messages from Torch Beach Volleyball.
        </p>

        <Section title="Call to Action / Where consent is collected">
          <p>
            Consent is collected during online account registration at{' '}
            <strong style={{ color: GOLD }}>app.torchvolleyball.com/register</strong>, on the final
            step of the registration form. Creating an account requires signing in first, so the
            form itself is not publicly viewable — the exact content of that step is reproduced
            below.
          </p>
          <p>
            Consent is also collected in person at our facility ({branding.facilityName}, Orlando, FL) on a
            paper registration form carrying the same disclosures.
          </p>
          <p>
            SMS consent is <strong>optional</strong> and is never a condition of registration,
            purchase, or participation. The checkbox is <strong>unchecked by default</strong> and
            must be actively selected by the user.
          </p>
        </Section>

        <Section title="Exactly what the member sees">
          {/* Mirrors the SMS step of the registration form verbatim. */}
          <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 8, padding: 20 }}>
            <div style={{ fontWeight: 700, marginBottom: 8, color: 'white' }}>SMS Notifications (Optional)</div>
            <p style={{ margin: '0 0 16px' }}>
              Torch Beach Volleyball LLC may send you automated text messages regarding session
              balance alerts, schedule updates, and club announcements. Message and data rates may
              apply. You can opt out at any time by replying STOP. View our{' '}
              <Link to="/sms-terms" style={{ color: GOLD }}>SMS Terms &amp; Conditions</Link>.
            </p>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <input type="checkbox" disabled readOnly style={{ marginTop: 4, width: 18, height: 18 }} />
              <span>
                I consent to receive SMS text messages from Torch Beach Volleyball (optional). See{' '}
                <Link to="/sms-terms" style={{ color: GOLD }}>SMS Terms</Link>.
              </span>
            </label>
          </div>
          <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', marginTop: 10 }}>
            Shown for review purposes — this copy is disabled. The live control appears in the
            registration form.
          </p>
        </Section>

        <Section title="Message types and frequency">
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>Session balance alerts (e.g. low or zero remaining sessions)</li>
            <li>Schedule updates, including class cancellations for weather</li>
            <li>Club announcements and program information</li>
          </ul>
          <p>
            Message frequency varies with club activity. These are account notifications, not
            marketing campaigns.
          </p>
        </Section>

        <Section title="Opt-out and help">
          <p>
            Reply <strong>STOP</strong> to any message to opt out at any time. Reply{' '}
            <strong>HELP</strong> for assistance, or contact us at{' '}
            <a href="mailto:info@torchvolleyball.com" style={{ color: GOLD }}>info@torchvolleyball.com</a>.
            Members can also turn SMS off at any time from their account profile.
          </p>
          <p><strong>Message and data rates may apply.</strong> Carriers are not liable for delayed or undelivered messages.</p>
        </Section>

        <Section title="Privacy">
          <p>
            Mobile phone numbers and SMS opt-in consent are <strong>not shared with, or sold to,
            third parties or affiliates for marketing or promotional purposes</strong>. Consent is
            used solely to deliver the notifications described above.
          </p>
        </Section>

        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
          <Link to="/sms-terms" style={{ color: GOLD }}>SMS Terms &amp; Conditions</Link>
          <Link to="/privacy" style={{ color: GOLD }}>Privacy Policy</Link>
          <Link to="/terms" style={{ color: GOLD }}>Terms of Service</Link>
          <Link to="/" style={{ color: 'rgba(255,255,255,0.6)' }}>Home</Link>
        </div>
      </div>
    </div>
  );
}

export default SmsOptIn;
